// src/components/exercise/ExercisePanel.tsx
import React, { useEffect, useState } from 'react';
import { useExerciseStore } from '../../stores/useExerciseStore';
import type { Exercise, ExerciseUserInput, GradeResult } from '../../types/exercise';
import ExerciseFooter from './ExerciseFooter';
import AudioPlayer from './AudioPlayer';
import TrueFalseExercise from './exercises/TrueFalseExercise';
import MultipleChoiceExercise from './exercises/MultipleChoiceExercise';
import FillBlankExercise from './exercises/FillBlankExercise';
import WritingExercise from './exercises/WritingExercise';
import MatchingExercise from './exercises/MatchingExercise';
import SentenceRearrangementExercise from './exercises/SentenceRearrangementExercise';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore }   from '../../stores/useThemeStore';

interface BodyProps {
  exercise: Exercise;
  onInputChange: (input: ExerciseUserInput) => void;
  isSubmitted: boolean;
  grade: GradeResult | null;
}

const ExerciseBody: React.FC<BodyProps> = ({ exercise, onInputChange, isSubmitted, grade }) => {
  const C = useThemePalette();
  const sharedProps = { exercise, onChange: onInputChange, isSubmitted, grade };
  switch (exercise.type) {
    case 'true_false':
    case 'true_false_correction':  return <TrueFalseExercise {...sharedProps} />;
    case 'multiple_choice':        return <MultipleChoiceExercise {...sharedProps} />;
    case 'fill_blank':             return <FillBlankExercise {...sharedProps} />;
    case 'writing_comprehension':  return <WritingExercise {...sharedProps} />;
    case 'matching':               return <MatchingExercise {...sharedProps} />;
    case 'sentence_rearrangement': return <SentenceRearrangementExercise {...sharedProps} />;
    case 'categorization':         return <MultipleChoiceExercise {...sharedProps} />;
    default:
      return <div style={{ padding: '20px', color: C.muted, textAlign: 'center' }}>Unknown exercise type</div>;
  }
};

function getSectionColor(sectionId: string): string {
  const map: Record<string, string> = {
    reading1: '#4f8cf7', vocabulary: '#8b5cf6', grammar: '#f59e0b',
    video: '#ec4899', reading2: '#06b6d4', audio: '#34d399',
  };
  return map[sectionId] ?? '#4f8cf7';
}

const ExercisePanel: React.FC = () => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const currentSectionId     = useExerciseStore(s => s.currentSectionId);
  const currentExerciseIndex = useExerciseStore(s => s.currentExerciseIndex);
  const totalExercises       = useExerciseStore(s => s.totalExercises);
  const score                = useExerciseStore(s => s.score);
  const wrongCount           = useExerciseStore(s => s.wrongCount);
  const currentUnitId        = useExerciseStore(s => s.currentUnitId);
  const exerciseData         = useExerciseStore(s => s.exerciseData);
  const submitAnswer         = useExerciseStore(s => s.submitAnswer);
  const nextExercise         = useExerciseStore(s => s.nextExercise);
  const prevExercise         = useExerciseStore(s => s.prevExercise);

  const [currentInput, setCurrentInput] = useState<ExerciseUserInput | null>(null);
  const [grade,        setGrade]        = useState<GradeResult | null>(null);
  const [isSubmitted,  setIsSubmitted]  = useState(false);

  useEffect(() => {
    setCurrentInput(null);
    setGrade(null);
    setIsSubmitted(false);
  }, [currentExerciseIndex, currentSectionId]);

  const unit     = currentUnitId ? exerciseData[currentUnitId] : null;
  const section  = unit?.sections.find(s => s.id === currentSectionId);
  const exercise = section?.exercises[currentExerciseIndex] ?? null;

  if (!exercise || !section) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: '0.9rem' }}>
        Loading exercise…
      </div>
    );
  }

  const handleSubmit = () => {
    if (!currentInput || isSubmitted) return;
    const result = submitAnswer(currentInput);
    if (result) { setGrade(result); setIsSubmitted(true); }
  };

  const isLastExercise = currentExerciseIndex >= totalExercises - 1;
  const canSubmit = (() => {
    if (!currentInput) return false;
    if (currentInput.type === 'multiple_choice') return currentInput.selectedIndex !== null;
    if (currentInput.type === 'true_false' || currentInput.type === 'true_false_correction') return currentInput.value !== null;
    if (currentInput.type === 'fill_blank') return currentInput.value.trim().length > 0;
    if (currentInput.type === 'writing_comprehension') return currentInput.value.trim().length > 0;
    if (currentInput.type === 'matching') return Object.keys(currentInput.selections).length > 0;
    if (currentInput.type === 'sentence_rearrangement') return currentInput.value.trim().length > 0;
    return true;
  })();

  const sectionColor = getSectionColor(currentSectionId ?? '');
  const progressPct  = totalExercises > 0
    ? ((currentExerciseIndex + (isSubmitted ? 1 : 0)) / totalExercises) * 100
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding:       '14px 20px',
        borderBottom:  `1px solid ${C.border}`,
        background:    isDark ? 'rgba(13,18,32,0.6)' : 'rgba(248,250,252,0.9)',
        flexShrink:    0,
        display:       'flex',
        flexDirection: 'column',
        gap:           '10px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{
            fontSize: '0.68rem', fontWeight: 700, color: sectionColor,
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>
            {section.title}
          </div>
          <div style={{ fontSize: '0.78rem', color: C.muted }}>
            <span style={{ color: C.text, fontWeight: 700 }}>{currentExerciseIndex + 1}</span>
            {' / '}
            <span style={{ color: C.text, fontWeight: 700 }}>{totalExercises}</span>
          </div>
        </div>

        {/* Progress bar + pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ flex: 1, height: '3px', background: C.border, borderRadius: '99px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${sectionColor}, ${C.accent})`,
              borderRadius: '99px',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <ScorePill label="✓" value={score}      color={C.success} />
          <ScorePill label="✗" value={wrongCount} color={C.error} />
        </div>
      </div>

      {/* Audio player */}
      {currentSectionId === 'audio' && (
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <AudioPlayer />
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 20px' }}>
        <div key={`${currentSectionId}-${currentExerciseIndex}`}>
          <ExerciseBody
            exercise={exercise}
            onInputChange={setCurrentInput}
            isSubmitted={isSubmitted}
            grade={grade}
          />
        </div>
      </div>

      {/* Footer */}
      <ExerciseFooter
        onPrev={prevExercise}
        onSubmit={handleSubmit}
        onNext={nextExercise}
        canSubmit={canSubmit}
        isSubmitted={isSubmitted}
        hasPrev={currentExerciseIndex > 0}
        isLastExercise={isLastExercise}
      />
    </div>
  );
};

const ScorePill: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '3px',
    padding: '2px 8px', borderRadius: '999px',
    background: `${color}14`, border: `1px solid ${color}30`,
    fontSize: '0.72rem', fontWeight: 700, color, flexShrink: 0,
  }}>
    {label} {value}
  </div>
);

export default ExercisePanel;
